import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { useAuth } from '../../../hooks/useAuth';
import { apiFetch } from '../../../api/client';
import { PageMeta } from '../shared/PageMeta';
import type { UserProfile } from '@spi/shared';

const profileSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phone: z.string().optional(),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(8),
  confirmPassword: z.string(),
}).refine((d) => d.newPassword === d.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

function LocationEditor({ latitude, longitude, onSave }: {
  latitude: number | null;
  longitude: number | null;
  onSave: (lat: number, lng: number) => Promise<void>;
}) {
  const [lat, setLat] = useState(latitude?.toString() ?? '');
  const [lng, setLng] = useState(longitude?.toString() ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    if (isNaN(latNum) || isNaN(lngNum) || latNum < -90 || latNum > 90 || lngNum < -180 || lngNum > 180) {
      toast.error('Please enter valid coordinates (lat: -90 to 90, lng: -180 to 180)');
      return;
    }
    setSaving(true);
    try {
      await onSave(latNum, lngNum);
      toast.success('Location updated');
    } catch {
      toast.error('Failed to update location');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card p-6">
      <h3 className="text-xl mb-2">Map Location</h3>
      <p className="text-sm text-spi-slate mb-4">
        Set your organization's coordinates to appear on the marketplace map.
      </p>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="input-label">Latitude</label>
          <input
            type="number"
            step="any"
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            className="input"
            placeholder="e.g., 0.3476"
          />
        </div>
        <div>
          <label className="input-label">Longitude</label>
          <input
            type="number"
            step="any"
            value={lng}
            onChange={(e) => setLng(e.target.value)}
            className="input"
            placeholder="e.g., 32.5825"
          />
        </div>
      </div>
      {latitude !== null && longitude !== null && (
        <p className="text-xs text-spi-slate mt-2">
          Current: {latitude.toFixed(4)}, {longitude.toFixed(4)}
        </p>
      )}
      <button
        onClick={handleSave}
        disabled={saving || !lat || !lng}
        className="btn btn-primary mt-4"
      >
        {saving ? 'Saving...' : 'Update Location'}
      </button>
    </div>
  );
}

export function ProfilePage() {
  const { user, setUser } = useAuth();

  const profileForm = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',
    },
  });

  const passwordForm = useForm({
    resolver: zodResolver(passwordSchema),
  });

  const updateProfile = async (data: { firstName: string; lastName: string; phone?: string }) => {
    try {
      const updated = await apiFetch<UserProfile>('/api/users/me', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      setUser(updated);
      toast.success('Profile updated');
    } catch {
      toast.error('Failed to update profile');
    }
  };

  const changePassword = async (data: { currentPassword: string; newPassword: string }) => {
    try {
      await apiFetch('/api/users/me/password', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      toast.success('Password changed');
      passwordForm.reset();
    } catch {
      toast.error('Failed to change password');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <PageMeta title="Profile" description="Manage your account profile" path="/marketplace/profile" />
      <h2 className="text-3xl mb-6">Profile</h2>

      <div className="max-w-2xl space-y-6">
        <div className="card p-6">
          <h3 className="text-xl mb-4">Personal Information</h3>
          <form onSubmit={profileForm.handleSubmit(updateProfile)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="input-label">First Name</label>
                <input {...profileForm.register('firstName')} className="input" />
              </div>
              <div>
                <label className="input-label">Last Name</label>
                <input {...profileForm.register('lastName')} className="input" />
              </div>
            </div>
            <div>
              <label className="input-label">Email</label>
              <input value={user?.email || ''} disabled className="input bg-gray-50" />
            </div>
            <div>
              <label className="input-label">Phone</label>
              <input {...profileForm.register('phone')} className="input" />
            </div>
            <button type="submit" disabled={profileForm.formState.isSubmitting} className="btn btn-primary">
              Save Changes
            </button>
          </form>
        </div>

        <div className="card p-6">
          <h3 className="text-xl mb-4">Organization</h3>
          <div className="space-y-2 text-sm">
            <p><span className="font-semibold text-spi-slate">Name:</span> {user?.organization?.name}</p>
            <p><span className="font-semibold text-spi-slate">Type:</span> {user?.organization?.type}</p>
            <p><span className="font-semibold text-spi-slate">Country:</span> {user?.organization?.country}</p>
            {user?.organization?.website && <p><span className="font-semibold text-spi-slate">Website:</span> {user.organization.website}</p>}
          </div>
        </div>

        <LocationEditor
          latitude={user?.organization?.latitude ?? null}
          longitude={user?.organization?.longitude ?? null}
          onSave={async (lat, lng) => {
            const updated = await apiFetch<UserProfile>('/api/users/me/organization', {
              method: 'PUT',
              body: JSON.stringify({ latitude: lat, longitude: lng }),
            });
            setUser(updated);
          }}
        />

        <div className="card p-6">
          <h3 className="text-xl mb-4">Change Password</h3>
          <form onSubmit={passwordForm.handleSubmit(changePassword)} className="space-y-4">
            <div>
              <label className="input-label">Current Password</label>
              <input {...passwordForm.register('currentPassword')} type="password" className="input" />
            </div>
            <div>
              <label className="input-label">New Password</label>
              <input {...passwordForm.register('newPassword')} type="password" className="input" />
              {passwordForm.formState.errors.newPassword && (
                <p className="text-red-500 text-xs mt-1">{passwordForm.formState.errors.newPassword.message}</p>
              )}
            </div>
            <div>
              <label className="input-label">Confirm New Password</label>
              <input {...passwordForm.register('confirmPassword')} type="password" className="input" />
              {passwordForm.formState.errors.confirmPassword && (
                <p className="text-red-500 text-xs mt-1">{passwordForm.formState.errors.confirmPassword.message}</p>
              )}
            </div>
            <button type="submit" disabled={passwordForm.formState.isSubmitting} className="btn btn-primary">
              Change Password
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
